// Zapas do pracy bez internetu (aplikacja ze sklepu, docs/pwa/NATYWNA.md 4 „Offline”): po starcie z siecią w tle
// pobiera to, co ma działać bez internetu, także ekrany, których klient jeszcze nie otwierał. Pyta tymi samymi
// funkcjami i adresami co ekrany, więc zaplecze.ts zapisuje odpowiedzi w api/offline.ts dokładnie tak, jak przy
// otwarciu ekranu. Zakres: alarmy z 30 dni; przepisy - lista każdej dziedziny i zakładki, przy subskrypcji WSZYSTKIE
// przepisy biblioteki z PDF i zdjęciami (właściciel 01.11 - klient może akurat robić dowolny), własne i udostępnione
// z treścią; plany; instrukcje (kroki, zdjęcia i PDF instrukcji urządzeń konta); Statystyki każdej sztuki z historią
// (wykres 24 h / 7 / 30 dni / rok, lista przebiegów, szczegóły i wykres przebiegów z 30 dni). Koniec subskrypcji dziedziny = pliki jej biblioteki precz (bez sieci pilnuje tego
// offline.ts BibliotekaOffline). Osobny moduł, bo nic istniejącego nie pobiera danych z wyprzedzeniem (ekrany/
// ladowanie.ts doladujWTle dociąga tylko kod ekranów). Pliki i zamknięte przebiegi świeższe niż ODSWIEZ_MS - bez
// ponownego pobrania; zapytania po kolei, żeby nie dławić startu APP.
import type { DeviceOut, DziedzinaPrzepisu, RecipeOut, RodzajPrzepisow } from "../gen/kontrakt";
import { natywna } from "../uklad/instalacja";
import { maWykres } from "../linie/statystyki";
import { odczyt, subskrybujPolaczenie, subskrybujSklad } from "../brama/magazyn";
import { pobierzPrzebiegi, przebieg } from "../brama/przebiegi";
import { pobierzProbki } from "../brama/probki";
import { PRZYCISKI } from "../ekrany/statystyki/okres";
import { adresMedium, instrukcja, instrukcje, pdfInstrukcji } from "./instrukcje";
import { KLUCZ_BIBLIOTEKI, czasWpisu, odczytajJson, zapamietajJson, zapomnijPrefiks, type BibliotekaOffline } from "./offline";
import { wczytajPlany } from "./plany";
import { BEZ_FILTRA as WSZYSTKIE_ALARMY, alarmy } from "./powiadomienia";
import { BEZ_FILTRA as WSZYSTKIE_PRZEPISY, formularz, kosztPrzepisu, przepisy, sekcja, skladnikiNa, zdjeciePrzepisu } from "./przepisy";
import { plik } from "./zaplecze";

const DNI_30_S = 30 * 24 * 3600;
const ODSWIEZ_MS = 7 * 24 * 3600_000;
const OPOZNIENIE_MS = 5000;
const STRON = 20; // bezpiecznik pętli stron
const DZIEDZINY: DziedzinaPrzepisu[] = ["wedzarnia", "trunki"];
const ZAKLADKI: RodzajPrzepisow[] = ["library", "own", "shared"];

let bieg = 0; // numer biegu - zatrzymanie (wylogowanie, inne konto) przerywa poprzedni

const swiezy = async (sciezka: string) => Date.now() - (await czasWpisu(sciezka)) < ODSWIEZ_MS;

async function plikRaz(sciezka: string): Promise<void> {
  if (!(await swiezy(sciezka))) await plik(sciezka).catch(() => undefined);
}

async function zapasAlarmow(nr: number): Promise<void> {
  const granica = Date.now() / 1000 - DNI_30_S;
  for (let s = 0; s < STRON && nr === bieg; s++) {
    const d = await alarmy(WSZYSTKIE_ALARMY, s).catch(() => null);
    const ostatni = d?.alarmy[d.alarmy.length - 1];
    if (!d?.wiecej || !ostatni || ostatni.kiedy < granica) return;
  }
}

/** Przepisy dziedziny (wszystkie zakładki i strony) i jej subskrypcja; null - zaplecze nie odpowiedziało. */
async function listaDziedziny(dziedzina: DziedzinaPrzepisu): Promise<{ aktywny: boolean; do: number; lista: RecipeOut[] } | null> {
  const f = { ...WSZYSTKIE_PRZEPISY, dziedzina };
  if (!(await przepisy(f).catch(() => null))) return null; // bez zakładki - szukajka i „Zaplanuj”
  const lista = new Map<number, RecipeOut>();
  let dostep = { aktywny: false, do: 0 };
  for (const rodzaj of ZAKLADKI) {
    const d = await przepisy(f, rodzaj).catch(() => null);
    if (!d) return null;
    dostep = { aktywny: d.dostep.aktywny, do: d.dostep.do ?? Number.MAX_SAFE_INTEGER };
    const s = d.sekcje[rodzaj];
    for (const p of s.przepisy) lista.set(p.id, p);
    for (let strona = 1, ile = s.przepisy.length; ile < s.wszystkie && strona < STRON; strona++) {
      const dalej = await sekcja(rodzaj, f, strona).catch(() => null);
      if (!dalej?.przepisy.length) break;
      ile += dalej.przepisy.length;
      for (const p of dalej.przepisy) lista.set(p.id, p);
    }
  }
  return { ...dostep, lista: [...lista.values()] };
}

async function zapasPrzepisow(nr: number): Promise<void> {
  const biblioteka: BibliotekaOffline = { ...(await odczytajJson<BibliotekaOffline>(KLUCZ_BIBLIOTEKI))?.dane };
  for (const dziedzina of DZIEDZINY) {
    const d = await listaDziedziny(dziedzina);
    if (!d || nr !== bieg) return;
    const ids = d.aktywny ? d.lista.filter((p) => p.biblioteka).map((p) => p.id) : [];
    // Koniec subskrypcji albo przepis wycofany z biblioteki - jego pliki z telefonu precz.
    for (const id of biblioteka[dziedzina]?.ids ?? []) if (!ids.includes(id)) await zapomnijPrefiks(`/api/przepisy/${id}/`);
    if (d.aktywny) biblioteka[dziedzina] = { do: d.do, ids };
    else delete biblioteka[dziedzina];
    await zapamietajJson(KLUCZ_BIBLIOTEKI, biblioteka); // straż końca subskrypcji przed pobraniem plików
    for (const p of d.lista) {
      if (nr !== bieg) return;
      await plikRaz(`/api/przepisy/${p.id}/pdf`);
      for (let i = 0; i < p.zdjecia; i++) await plikRaz(zdjeciePrzepisu(p.id, i));
      await skladnikiNa(p.id, 1).catch(() => null); // przelicznik „Robię: X kg” bez sieci liczy z tego (przepisy.ts)
      await kosztPrzepisu(p.id).catch(() => null);
      if (!p.biblioteka) await formularz(p.id).catch(() => null); // szczegóły własnego i udostępnionego
    }
  }
}

async function zapasInstrukcji(nr: number): Promise<void> {
  for (const m of (await instrukcje().catch(() => null))?.instrukcje ?? []) {
    if (nr !== bieg) return;
    if (m.okladka) await plikRaz(adresMedium(m.id, m.okladka));
    if (!m.twoje) continue;
    const i = await instrukcja(m.id).catch(() => null);
    for (const k of i?.instrukcja.rozdzialy.flatMap((r) => r.kroki) ?? []) {
      for (const media of [k.obraz, k.animacja]) if (media) await plikRaz(adresMedium(m.id, media));
    }
    for (let n = 0; n < m.pdf.length; n++) await pdfInstrukcji(m.id, n).catch(() => null);
  }
}

/** Skład konta z bramy po połączeniu (sztuki do przebiegów); null - zapas zatrzymany. */
function skladZBramy(nr: number): Promise<readonly DeviceOut[] | null> {
  return new Promise((ok) => {
    const odpnij: (() => void)[] = [];
    const sprawdz = () => {
      if (nr !== bieg) ok(null);
      else if (odczyt.polaczenie() === "jest" && odczyt.wczytana()) ok(odczyt.sklad());
      else return false;
      for (const f of odpnij) f();
      return true;
    };
    if (!sprawdz()) odpnij.push(subskrybujSklad(sprawdz), subskrybujPolaczenie(sprawdz));
  });
}

async function zapasPrzebiegow(nr: number): Promise<void> {
  const teraz = Date.now() / 1000, granica = teraz - DNI_30_S;
  for (const d of (await skladZBramy(nr))?.filter((x) => maWykres(x.typ)) ?? []) {
    const adres = `/brama/urzadzenia/${encodeURIComponent(d.sn)}`;
    // Wykres Statystyk: okresy z przycisków (offline.ts przycina najlepiej pokrywający zapis do zakresu ekranu).
    for (const [, , s] of PRZYCISKI) if (nr === bieg) await pobierzProbki(d.sn, teraz - s, teraz).catch(() => null);
    for (let s = 0; s < STRON && nr === bieg; s++) {
      const o = await pobierzPrzebiegi(d.sn, { program: "", status: "" }, s).catch(() => null);
      for (const r of o?.przebiegi.filter((x) => x.start >= granica) ?? []) {
        if (r.koniec != null && await swiezy(`${adres}/przebiegi/${r.id}`)) continue;
        await przebieg(d.sn, r.id).catch(() => null);
        // Wykres przebiegu - ten sam zakres co statystyki/Szczegoly.tsx (start i koniec z marginesem 2%).
        const koniec = r.koniec ?? r.start + r.czas, m = Math.max(60, (koniec - r.start) * 0.02);
        await pobierzProbki(d.sn, r.start - m, koniec + m).catch(() => null);
      }
      if (!o?.wiecej || (o.przebiegi[o.przebiegi.length - 1]?.start ?? 0) < granica) break;
    }
  }
}

/** Po zalogowaniu (PowlokaAplikacji): zapas w tle - od razu przy sieci, inaczej gdy wróci. */
export function zrobZapas(): void {
  if (!natywna()) return;
  const nr = ++bieg;
  const start = () => {
    removeEventListener("online", start);
    window.setTimeout(() => {
      if (nr !== bieg) return;
      void (async () => {
        await zapasAlarmow(nr);
        await wczytajPlany().catch(() => undefined);
        await zapasPrzepisow(nr);
        await zapasInstrukcji(nr);
        await zapasPrzebiegow(nr);
      })();
    }, OPOZNIENIE_MS);
  };
  if (navigator.onLine) start();
  else addEventListener("online", start);
}

export function zatrzymajZapas(): void {
  bieg++;
}
